// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Stack, Text } from '@mantine/core';
import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { MEDPLUM_BOT_SYSTEM } from '@medplum/scriptsure-react';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { FormCard } from '../../../components/FormCard';
import { getIdentifier, getTelecom, NPI_SYSTEM } from '../../../utils/practitioner';

type ScriptSureInviteStatus = 'invite_generated' | 'invite_pending' | 'already_enrolled' | 'synced';

const SCRIPTSURE_PROVIDER_INVITE_BOT = {
  system: MEDPLUM_BOT_SYSTEM,
  value: 'scriptsure-provider-invite-bot',
} as const;

interface ScriptSureInviteResult {
  status: ScriptSureInviteStatus;
  inviteUrl?: string;
  practitionerEmail?: string;
}

function isScriptSureInviteResult(value: unknown): value is ScriptSureInviteResult {
  if (!value || typeof value !== 'object' || !('status' in value)) {
    return false;
  }

  return ['invite_generated', 'invite_pending', 'already_enrolled', 'synced'].includes(String(value.status));
}

function getMissingRequirements(practitioner: Practitioner): string[] {
  const name = practitioner.name?.[0];
  const missing: string[] = [];
  if (!name?.given?.[0]?.trim() || !name.family?.trim()) {
    missing.push('full name');
  }
  if (!getTelecom(practitioner, 'email').trim()) {
    missing.push('email address');
  }
  if (!getIdentifier(practitioner, NPI_SYSTEM).trim()) {
    missing.push('NPI');
  }
  return missing;
}

interface ScriptSureProviderSetupSectionProps {
  practitioner: Practitioner;
}

/**
 * Starts ScriptSure's provider-owned enrollment flow. This is a Medplum system
 * integration bot, so it intentionally uses its documented identifier rather
 * than the environment-prefixed identifiers used by Polaris-owned bots.
 */
export function ScriptSureProviderSetupSection({ practitioner }: ScriptSureProviderSetupSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [result, setResult] = useState<ScriptSureInviteResult>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const missingRequirements = getMissingRequirements(practitioner);
  const canStart = Boolean(practitioner.id) && missingRequirements.length === 0;

  const handleStartSetup = async (): Promise<void> => {
    if (!practitioner.id || !canStart) {
      return;
    }

    setLoading(true);
    setError(undefined);
    try {
      const response = await medplum.executeBot(SCRIPTSURE_PROVIDER_INVITE_BOT, {
        practitionerId: practitioner.id,
      });
      if (!isScriptSureInviteResult(response)) {
        throw new Error('ScriptSure returned an unexpected provider invite response.');
      }
      setResult(response);
    } catch (reason) {
      Sentry.captureException(reason, { tags: { integration: 'scriptsure', operation: 'provider-invite' } });
      setError('We could not start ScriptSure setup. Verify your provider profile and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormCard
      title="ScriptSure prescribing"
      action={
        <Button size="xs" loading={loading} disabled={!canStart} onClick={handleStartSetup}>
          Start ScriptSure setup
        </Button>
      }
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Start a secure ScriptSure invitation using the email address in your provider profile. Complete the enrollment
          and identity-proofing steps in ScriptSure before prescribing medication.
        </Text>
        {!canStart && (
          <Alert color="yellow" title="Provider profile needs attention">
            Add your {missingRequirements.join(', ')} in Provider Information before starting ScriptSure setup.
          </Alert>
        )}
        {result?.status === 'invite_generated' && result.inviteUrl && (
          <Alert color="blue" title="Complete ScriptSure setup">
            <Stack gap="sm">
              <Text size="sm">
                Continue enrollment in ScriptSure{result.practitionerEmail ? ` for ${result.practitionerEmail}` : ''}.
              </Text>
              <Button component="a" href={result.inviteUrl} target="_blank" rel="noreferrer" w="fit-content">
                Continue to ScriptSure
              </Button>
            </Stack>
          </Alert>
        )}
        {result?.status === 'invite_pending' && (
          <Alert color="blue" title="ScriptSure invitation pending">
            Complete the enrollment link already sent to your provider email address.
          </Alert>
        )}
        {(result?.status === 'already_enrolled' || result?.status === 'synced') && (
          <Alert color="green" title="ScriptSure setup complete">
            Your provider profile is connected to ScriptSure. You can now open a patient chart and select Prescribe.
          </Alert>
        )}
        {error && (
          <Alert color="red" title="Unable to start ScriptSure setup">
            {error}
          </Alert>
        )}
      </Stack>
    </FormCard>
  );
}
